import { useDndMonitor } from '@dnd-kit/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  footprintShapeGridDelta,
  getDraggedFootprintShapeIndex,
  isSameGridDelta,
  resolveFootprintShapeDrop
} from 'utils/footprint-shape-drag';
import { FootprintShapeDragPreview } from 'utils/footprint-shape-drag-types';
import { translateShape } from 'utils/footprint-shapes-editing';
import { UseFootprintShapeDragParams } from './use-footprint-shape-drag-types';

const ZERO_DELTA = { x: 0, y: 0 };

export function useFootprintShapeDrag({
  shapes,
  zoom,
  onShapeChange,
  onDeleteShape,
  onDragStart,
  onDragEnd
}: UseFootprintShapeDragParams): FootprintShapeDragPreview | null {
  const [preview, setPreview] = useState<FootprintShapeDragPreview | null>(
    null
  );
  const previewRef = useRef<FootprintShapeDragPreview | null>(null);

  const updatePreview = useCallback(
    (next: FootprintShapeDragPreview | null) => {
      previewRef.current = next;
      setPreview(next);
    },
    []
  );

  const finishDrag = useCallback(() => {
    document.body.style.cursor = '';
    updatePreview(null);
    onDragEnd?.();
  }, [updatePreview, onDragEnd]);

  useEffect(
    () => () => {
      if (previewRef.current) document.body.style.cursor = '';
    },
    []
  );

  useDndMonitor({
    onDragStart: ({ active }) => {
      const shapeIndex = getDraggedFootprintShapeIndex(active.data.current);
      if (shapeIndex === null) return;
      document.body.style.cursor = 'grabbing';
      updatePreview({ shapeIndex, delta: ZERO_DELTA });
      onDragStart?.();
    },
    onDragMove: ({ active }) => {
      const shapeIndex = getDraggedFootprintShapeIndex(active.data.current);
      if (shapeIndex === null) return;
      const delta = footprintShapeGridDelta(
        active.rect.current.initial,
        active.rect.current.translated,
        zoom
      );
      if (isSameGridDelta(previewRef.current?.delta, delta)) return;
      updatePreview({ shapeIndex, delta });
    },
    onDragEnd: ({ active, over }) => {
      const shapeIndex = getDraggedFootprintShapeIndex(active.data.current);
      if (shapeIndex === null) return;
      const action = resolveFootprintShapeDrop(
        shapeIndex,
        over,
        previewRef.current?.delta ?? ZERO_DELTA
      );
      finishDrag();
      if (action.kind === 'delete') {
        onDeleteShape(action.shapeIndex);
        return;
      }
      const shape = shapes[shapeIndex];
      if (action.kind !== 'move' || !shape) return;
      onShapeChange(
        shapeIndex,
        translateShape(shape.points, action.delta.x, action.delta.y),
        shape.vertexEdited === true
      );
    },
    onDragCancel: ({ active }) => {
      if (getDraggedFootprintShapeIndex(active.data.current) === null) return;
      finishDrag();
    }
  });

  return preview;
}
